import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Check } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

/**
 * Supplier autocomplete — searches the /suppliers collection as the user types.
 * If the exact typed name doesn't exist, offers an inline "+ Create '<name>'"
 * option that POSTs to /suppliers so the field is never a dead-end.
 *
 * Props:
 *   value           string  — currently selected supplier name
 *   supplierId      string  — currently selected supplier id (optional)
 *   onChange({ name, id })  — invoked whenever the selection changes
 *   testId          string  — data-testid for the input
 *   placeholder     string
 */
export function SupplierAutocomplete({ value, supplierId, onChange, testId, placeholder }) {
  const [query, setQuery] = useState(value || "");
  const [suppliers, setSuppliers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);

  // Load once
  useEffect(() => {
    api.get("/suppliers")
      .then((rows) => { setSuppliers(rows || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  // Keep query synced when parent value changes externally
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close popover when clicking outside
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return suppliers.slice(0, 8);
    return suppliers.filter((s) => (s.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [query, suppliers]);

  const exactMatch = useMemo(
    () => suppliers.find((s) => (s.name || "").toLowerCase() === (query || "").trim().toLowerCase()),
    [suppliers, query],
  );

  const pick = (s) => {
    setQuery(s.name);
    onChange({ name: s.name, id: s.id });
    setOpen(false);
  };

  const createNew = async () => {
    const name = (query || "").trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await api.post("/suppliers", { name });
      setSuppliers([...suppliers, created]);
      onChange({ name: created.name, id: created.id });
      setQuery(created.name);
      toast.success(`Supplier "${created.name}" created`);
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        // Someone else created it — refresh and select
        try {
          const rows = await api.get("/suppliers");
          setSuppliers(rows || []);
          const existing = (rows || []).find((s) => (s.name || "").toLowerCase() === name.toLowerCase());
          if (existing) { pick(existing); return; }
        } catch { /* fall through */ }
      }
      toast.error(typeof detail === "string" ? detail : "Failed to create supplier");
    } finally { setCreating(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        data-testid={testId}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || "Search supplier..."}
        className={inputCls}
      />
      {open && loaded && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filtered.length === 0 && !query.trim() && (
            <div className="px-3 py-2 text-xs text-slate-400">Start typing to search suppliers…</div>
          )}
          {filtered.map((s) => (
            <button
              type="button"
              key={s.id}
              onClick={() => pick(s)}
              data-testid={`sa-opt-${s.id}`}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 flex items-center justify-between"
            >
              <span className="truncate">{s.name}</span>
              {supplierId === s.id && <Check className="w-3.5 h-3.5 text-emerald-600" />}
            </button>
          ))}
          {query.trim() && !exactMatch && (
            <button
              type="button"
              onClick={createNew}
              disabled={creating}
              data-testid="sa-create-new"
              className="w-full text-left px-3 py-2 text-sm border-t border-slate-100 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create supplier &ldquo;{query.trim()}&rdquo;
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default SupplierAutocomplete;
